/*
  ONF-CUSTOM-START
  alpha_cross-sell.js
  Legt einen Vorschlag aus dem Cross-Selling direkt in den Warenkorb.

  Warum eigenes JavaScript und nicht <product-form> des Themes: auf der
  Warenkorbseite stehen die Vorschlaege mitten in der Produktliste, und die
  steckt im Formular des Warenkorbs. Ein <form> im <form> wirft der Browser
  beim Einlesen der Seite weg - die erste Karte haette dann gar keinen
  funktionierenden Knopf. Deshalb ohne Formular.

  Ohne JavaScript bleibt der Knopf ein normaler Link auf die Produktseite mit
  der gewaehlten Variante. Nichts geht verloren, es sind nur zwei Klicks mehr.

  Nach dem Hinzufuegen wird dieselbe Mechanik benutzt wie im Theme: die
  betroffenen Sections werden neu vom Server geholt und ausgetauscht, und das
  Ereignis "cartUpdate" sagt den anderen Bausteinen Bescheid.
  ONF-CUSTOM-END
*/

if (!customElements.get('alpha-xs-add')) {
  class AlphaCrossSellAdd extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;

      this.button = this.querySelector('[data-xs-add]');
      this.errorBox = this.querySelector('[data-xs-error]');
      this.placement = this.dataset.placement || 'cart';
      if (!this.button) return;

      this.addEventListener('change', this.syncLink.bind(this));
      this.button.addEventListener('click', this.onClick.bind(this));
    }

    /** Das angeklickte Bedienelement: Pille oder Eintrag im Auswahlfeld. */
    get selected() {
      const checked = this.querySelector('[data-xs-variant]:checked');
      if (checked) return checked;

      const select = this.querySelector('select[data-xs-variant]');
      return select ? select.selectedOptions[0] : null;
    }

    /** Die gerade gewaehlte Variante: Pille, Auswahlfeld oder die einzige. */
    get variantId() {
      const selected = this.selected;
      return selected ? selected.value : this.dataset.variant;
    }

    /** Der Link zeigt immer auf die gewaehlte Variante - fuer alle, bei denen
        das JavaScript nicht laeuft, und fuer die Vorschau in der Statuszeile.

        Das Ereignis wird hier gestoppt: auf der Warenkorbseite stehen die
        Karten innerhalb von <cart-items>, und das Theme haelt dort jedes
        change-Ereignis fuer eine geaenderte Menge. */
    syncLink(event) {
      if (event) event.stopPropagation();
      this.syncImage();
      if (!this.dataset.url) return;

      const url = `${this.dataset.url}?variant=${this.variantId}`;
      this.button.href = url;
      const media = this.closest('.alpha-xs__item').querySelector('[data-xs-media]');
      if (media) media.href = url;
    }

    /** Bild zur gewaehlten Variante zeigen. Eine Farbe heisst bei jedem Kunden
        etwas anderes - das Bild klaert das, bevor bestellt wird. */
    syncImage() {
      const card = this.closest('.alpha-xs__item');
      // Ueber die Klasse, nicht ueber ein eigenes Attribut: image_tag des Themes
      // gibt Attribute mit Bindestrich nicht aus.
      const image = card && card.querySelector('.alpha-xs__image');
      const source = this.selected;
      if (!image || !source) return;

      const url = source.dataset.xsImage;
      if (!url || image.dataset.xsShown === url) return;

      image.dataset.xsShown = url;
      image.removeAttribute('srcset');
      image.removeAttribute('sizes');
      image.src = url;
    }

    /** Welche Bereiche der Seite sich nach dem Hinzufuegen aendern. */
    sectionsToRender() {
      if (this.placement === 'drawer') return ['cart-drawer', 'cart-icon-bubble'];

      const ids = [];
      document.querySelectorAll('#main-cart-items, #main-cart-footer').forEach((el) => {
        if (el.dataset.id) ids.push(el.dataset.id);
      });
      ids.push('cart-icon-bubble');
      return ids;
    }

    async onClick(event) {
      if (!window.fetch || this.busy) return;
      event.preventDefault();

      const id = this.variantId;
      if (!id) return;

      this.setBusy(true);
      this.showError('');

      try {
        const url = (window.routes && window.routes.cart_add_url) || '/cart/add';
        const response = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{ id: Number(id), quantity: 1 }],
            sections: this.sectionsToRender().join(','),
            sections_url: window.location.pathname,
          }),
        });
        const data = await response.json();

        if (data.status) {
          this.showError(data.description || data.message || '');
          return;
        }

        this.render(data);
      } catch (error) {
        console.error(error);
        // Wenn der Weg ueber das Netz nicht klappt, wenigstens auf die
        // Produktseite schicken statt den Kunden im Nichts stehen zu lassen.
        window.location.href = this.button.href;
      } finally {
        this.setBusy(false);
      }
    }

    render(data) {
      const sections = data.sections || {};
      const drawer = document.querySelector('cart-drawer');

      if (this.placement === 'drawer' && drawer && sections['cart-drawer']) {
        drawer.renderContents(data);
        this.zeigeNeuePosition(drawer);
      } else {
        this.replaceSection(sections, '#main-cart-items');
        this.replaceSection(sections, '#main-cart-footer');
        this.replaceSection(sections, '#cart-icon-bubble', 'cart-icon-bubble');
      }

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'alpha-cross-sell', cartData: data });
      }
    }

    /** Nach dem Hinzufuegen die Liste im Einschub an den Anfang holen. Neue
        Positionen stehen dort oben, und der Bereich ist nur ein paar Zeilen
        hoch - ohne das steht der neue Artikel ausserhalb des Bildes und es
        sieht aus, als waere nichts passiert. */
    zeigeNeuePosition(drawer) {
      // Das Theme rendert den Einschub neu, deshalb erst danach suchen.
      setTimeout(() => {
        const position = drawer.querySelector('.cart-item');
        if (!position) return;

        let bereich = position.parentElement;
        while (bereich && bereich !== drawer) {
          const style = getComputedStyle(bereich);
          const scrollt = /auto|scroll/.test(style.overflowY);
          if (scrollt && bereich.scrollHeight > bereich.clientHeight + 2) {
            bereich.scrollTo({ top: 0, behavior: 'smooth' });
            return;
          }
          bereich = bereich.parentElement;
        }
      }, 120);
    }

    replaceSection(sections, selector, sectionId) {
      const target = document.querySelector(selector);
      if (!target) return;

      const html = sections[sectionId || target.dataset.id];
      if (!html) return;

      const source = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
      target.innerHTML = source ? source.innerHTML : target.innerHTML;
    }

    setBusy(state) {
      this.busy = state;
      this.button.classList.toggle('loading', state);
      this.button.setAttribute('aria-busy', state ? 'true' : 'false');
      const spinner = this.button.querySelector('.loading__spinner');
      if (spinner) spinner.classList.toggle('hidden', !state);
    }

    showError(text) {
      if (!this.errorBox) return;
      this.errorBox.textContent = text;
      this.errorBox.hidden = text === '';
    }
  }

  customElements.define('alpha-xs-add', AlphaCrossSellAdd);
}

/*
  Fortschrittsbalken bis zum Mindestwert. Das Theme tauscht den Warenkorb nach
  jeder Aenderung per innerHTML aus, der Balken wird also jedes Mal neu
  gebaut. Er merkt sich deshalb den letzten Stand (je Ort: Warenkorbseite und
  Einschub getrennt, auch ueber einen Seitenwechsel hinweg) und laeuft von
  dort zum neuen Wert, statt einfach dazustehen.
*/
if (!customElements.get('alpha-xs-bar')) {
  const lastProgress = {};
  const storageKey = (key) => `alpha-xs-bar:${key}`;
  const readLast = (key) => {
    if (key in lastProgress) return lastProgress[key];
    try {
      const value = sessionStorage.getItem(storageKey(key));
      return value === null ? null : Number(value);
    } catch (error) {
      return null;
    }
  };
  const writeLast = (key, value) => {
    lastProgress[key] = value;
    try {
      sessionStorage.setItem(storageKey(key), String(value));
    } catch (error) {
      // Privater Modus o. ae.: dann eben nur fuer diese Seite merken.
    }
  };

  class AlphaCrossSellBar extends HTMLElement {
    connectedCallback() {
      const fill = this.querySelector('.alpha-xs-bar__fill');
      if (!fill) return;

      const key = this.dataset.key || 'cart';
      const target = Math.max(0, Math.min(100, Number(this.dataset.progress) || 0));
      const last = readLast(key);
      writeLast(key, target);

      const reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (last === null || last === target || reduced) return;

      // Ohne Uebergang auf den alten Stand, dann im naechsten Frame zum neuen.
      fill.style.transition = 'none';
      fill.style.width = `${last}%`;
      void fill.offsetWidth;
      fill.style.transition = '';
      requestAnimationFrame(() => {
        fill.style.width = `${target}%`;
      });
    }
  }

  customElements.define('alpha-xs-bar', AlphaCrossSellBar);
}
